import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileCheck, Send, Download, Paperclip, FileText, ScrollText, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatTimeOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";
import { RelatedDocuments } from "@/components/RelatedDocuments";
import { MEETING_ATTACHMENT_KINDS } from "@costadorada/shared";

interface AgendaItem {
  id: string;
  order: number;
  title: string;
  description?: string;
  status: string;
}

interface MinutesComment {
  id: string;
  content: string;
  createdAt: string;
  resolvedAt?: string | null;
  author: { firstName: string; lastName: string; role: string };
}

interface Minutes {
  id: string;
  draftContent: string;
  finalContent?: string | null;
  status: "DRAFT" | "UNDER_REVIEW" | "APPROVED" | "DISTRIBUTED";
  fileUrl?: string | null;
  approvedAt?: string | null;
  comments: MinutesComment[];
  createdBy: { firstName: string; lastName: string };
  approvedBy?: { firstName: string; lastName: string } | null;
}

type AttachmentKind = (typeof MEETING_ATTACHMENT_KINDS)[number];

interface MeetingAttachment {
  id: string;
  kind: AttachmentKind;
  title: string;
  fileUrl: string;
  createdAt: string;
  uploadedBy: { firstName: string; lastName: string };
}

interface MeetingDocument {
  id: string;
  title: string;
  documentType: string;
  version: string;
  fileUrl: string;
  isCurrentVersion: boolean;
  createdAt: string;
}

interface MeetingDetail {
  id: string;
  title: string;
  meetingType: string;
  scheduledAt: string;
  endTime?: string | null;
  status: string;
  location?: string;
  meetLink?: string;
  agendaItems: AgendaItem[];
  minutes?: Minutes | null;
  resolutions: Array<{ id: string; title: string; status: string }>;
  attachments: MeetingAttachment[];
  documents: MeetingDocument[];
  createdBy: { firstName: string; lastName: string };
}

export function MeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: meeting, isLoading, isError } = useQuery({
    queryKey: ["meeting", id],
    queryFn: async () => (await api.get<MeetingDetail>(`/meetings/${id}`)).data,
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: async (mid: string) => (await api.delete(`/meetings/${mid}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meetings"] });
      navigate("/meetings");
    },
  });

  const canEdit = user && (user.role === "ADMIN" || user.role === "BOARD_MEMBER");
  const isAdmin = user?.role === "ADMIN";

  if (isError) return <QueryError />;
  if (isLoading || !meeting) return <div className="text-slate-500">{t("common.loading")}</div>;

  return (
    <div className="space-y-6">
      <Link to="/meetings" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
        <ArrowLeft size={14} /> {t("meetings.title")}
      </Link>

      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="info">{meeting.meetingType}</Badge>
              <Badge>{meeting.status}</Badge>
            </div>
            <h1 className="text-2xl font-bold">{meeting.title}</h1>
            <div className="text-sm text-slate-600 mt-1">
              {formatDate(meeting.scheduledAt, i18n.language)}
              {meeting.endTime && <span className="text-slate-400"> – {formatTimeOnly(meeting.endTime, i18n.language)}</span>}
            </div>
            {meeting.location && <div className="text-sm text-slate-500">{meeting.location}</div>}
            {meeting.meetLink && (
              <a href={meeting.meetLink} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
                {meeting.meetLink}
              </a>
            )}
          </div>
          <AdminRecordActions onDelete={() => deleteMutation.mutate(meeting.id)} entityLabel="la reunión" />
        </div>
      </div>

      <AgendaSection meetingId={meeting.id} items={meeting.agendaItems} canEdit={!!canEdit} />

      <MinutesSection meetingId={meeting.id} minutes={meeting.minutes ?? null} canEdit={!!canEdit} onChanged={() => qc.invalidateQueries({ queryKey: ["meeting", id] })} />

      <AttachmentsSection
        meetingId={meeting.id}
        attachments={meeting.attachments ?? []}
        canEdit={!!canEdit}
        canDelete={!!isAdmin}
        onChanged={() => {
          qc.invalidateQueries({ queryKey: ["meeting", id] });
          qc.invalidateQueries({ queryKey: ["documents"] });
        }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MeetingDocumentsSection documents={meeting.documents ?? []} />
        <MeetingResolutionsSection resolutions={meeting.resolutions ?? []} />
      </div>
    </div>
  );
}

/**
 * Files and links kept with the meeting: the signed acta, a transcript or a
 * recording (e.g. the Fireflies links), the convocatoria, agenda, presentations,
 * reports… A "minutes file" is also published to Documents as this meeting's acta.
 */
function AttachmentsSection({
  meetingId,
  attachments,
  canEdit,
  canDelete,
  onChanged,
}: {
  meetingId: string;
  attachments: MeetingAttachment[];
  canEdit: boolean;
  canDelete: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
        <Paperclip size={18} /> {t("meetings.attachments")}
      </h2>
      <RelatedDocuments
        endpoint={`/meetings/${meetingId}/attachments`}
        items={attachments}
        kinds={MEETING_ATTACHMENT_KINDS}
        kindLabel={(k) => t(`meetings.attachmentKind.${k}`)}
        defaultKind="TRANSCRIPT"
        folder="costadorada1/minutes"
        canEdit={canEdit}
        canDelete={canDelete}
        onChanged={onChanged}
        emptyText={t("meetings.noAttachments")}
        highlightKinds={["MINUTES_FILE"]}
        noteForKind={(k) => (k === "MINUTES_FILE" ? t("meetings.minutesFileNote") : null)}
      />
    </div>
  );
}

function MeetingDocumentsSection({ documents }: { documents: MeetingDocument[] }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
        <FileText size={18} /> {t("meetings.meetingDocuments")}
      </h2>
      {documents.length === 0 ? (
        <p className="text-sm text-slate-500">—</p>
      ) : (
        <ul className="divide-y divide-slate-100 text-sm">
          {documents.map((d) => (
            <li key={d.id} className="py-2 flex items-center gap-2">
              <a href={d.fileUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline flex-1 min-w-0 truncate">
                {d.title}
              </a>
              <span className="text-xs text-slate-500">v{d.version}</span>
              {d.isCurrentVersion && <Badge variant="success">{t("legal.currentVersion")}</Badge>}
              <span className="text-xs text-slate-400">{formatDate(d.createdAt, i18n.language)}</span>
            </li>
          ))}
        </ul>
      )}
      <Link to="/documents?type=ACTA_JUNTA" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-3">
        {t("meetings.openInDocuments")} <ExternalLink size={11} />
      </Link>
    </div>
  );
}

function MeetingResolutionsSection({ resolutions }: { resolutions: Array<{ id: string; title: string; status: string }> }) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
        <ScrollText size={18} /> {t("meetings.meetingResolutions")}
      </h2>
      {resolutions.length === 0 ? (
        <p className="text-sm text-slate-500">—</p>
      ) : (
        <ul className="divide-y divide-slate-100 text-sm">
          {resolutions.map((r) => (
            <li key={r.id} className="py-2 flex items-center gap-2">
              <Link to="/resolutions" className="text-primary hover:underline flex-1 min-w-0 truncate">
                {r.title}
              </Link>
              <Badge>{r.status}</Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AgendaSection({ meetingId, items, canEdit }: { meetingId: string; items: AgendaItem[]; canEdit: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-3">{t("meetings.agenda")}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">—</p>
      ) : (
        <ol className="space-y-2 list-decimal pl-5">
          {items.map((it) => (
            <li key={it.id}>
              <div className="font-medium">{it.title}</div>
              {it.description && <div className="text-sm text-slate-600">{it.description}</div>}
              <Badge>{it.status}</Badge>
            </li>
          ))}
        </ol>
      )}
      {canEdit && (
        <p className="text-xs text-slate-400 mt-3">
          Para gestionar puntos de agenda, edite la reunión (próxima iteración: editor inline).
        </p>
      )}
    </div>
  );
}

function MinutesSection({
  meetingId,
  minutes,
  canEdit,
  onChanged,
}: {
  meetingId: string;
  minutes: Minutes | null;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState("");
  const [comment, setComment] = useState("");

  const createMutation = useMutation({
    mutationFn: async () => (await api.post(`/meetings/${meetingId}/minutes`, { draftContent: draft })).data,
    onSuccess: () => {
      onChanged();
      setDraft("");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: Partial<Minutes>) => (await api.put(`/meetings/${meetingId}/minutes`, payload)).data,
    onSuccess: onChanged,
  });

  const commentMutation = useMutation({
    mutationFn: async () => (await api.post(`/meetings/${meetingId}/minutes/comments`, { content: comment })).data,
    onSuccess: () => {
      setComment("");
      onChanged();
    },
  });

  const approveMutation = useMutation({
    mutationFn: async () => (await api.post(`/meetings/${meetingId}/minutes/approve`)).data,
    onSuccess: onChanged,
  });

  if (!minutes) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold mb-3">{t("meetings.minutes")}</h2>
        <p className="text-sm text-slate-500 mb-3">{t("meetings.noMinutes")}</p>
        {canEdit && (
          <>
            <textarea
              className="input min-h-[180px]"
              placeholder={t("meetings.draftContent")}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="mt-3 flex justify-end">
              <button
                className="btn-primary"
                disabled={!draft.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                <FileCheck size={16} /> {t("meetings.createMinutes")}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  const locked = minutes.status === "APPROVED" || minutes.status === "DISTRIBUTED";
  const statusLabel: Record<string, string> = {
    DRAFT: t("meetings.draftMinutes"),
    UNDER_REVIEW: t("meetings.minutesUnderReview"),
    APPROVED: t("meetings.minutesApproved"),
    DISTRIBUTED: t("meetings.minutesDistributed"),
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">{t("meetings.minutes")}</h2>
        <div className="flex items-center gap-2">
          <Badge variant={locked ? "success" : "warning"}>{statusLabel[minutes.status]}</Badge>
          {minutes.fileUrl && (
            <a href={minutes.fileUrl} download="acta.pdf" className="btn-secondary">
              <Download size={14} /> PDF
            </a>
          )}
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-2">
        Por {minutes.createdBy.firstName} {minutes.createdBy.lastName}
        {minutes.approvedBy && (
          <> · Aprobada por {minutes.approvedBy.firstName} {minutes.approvedBy.lastName} el {minutes.approvedAt && formatDate(minutes.approvedAt, i18n.language)}</>
        )}
      </div>

      {locked ? (
        <div className="whitespace-pre-wrap bg-slate-50 p-3 rounded border border-slate-200 text-sm">
          {minutes.finalContent ?? minutes.draftContent}
        </div>
      ) : (
        <>
          <textarea
            className="input min-h-[240px]"
            defaultValue={minutes.finalContent ?? minutes.draftContent}
            onBlur={(e) => {
              if (!canEdit) return;
              const val = e.target.value;
              if (val !== (minutes.finalContent ?? minutes.draftContent)) {
                updateMutation.mutate({ finalContent: val });
              }
            }}
          />
          {canEdit && (
            <div className="flex justify-end mt-3">
              <button className="btn-primary" disabled={approveMutation.isPending} onClick={() => approveMutation.mutate()}>
                <Send size={16} /> {t("meetings.approveMinutes")}
              </button>
            </div>
          )}
        </>
      )}

      <div className="mt-6">
        <h3 className="font-semibold mb-2">{t("meetings.comments")} ({minutes.comments.length})</h3>
        {minutes.comments.length === 0 ? (
          <p className="text-sm text-slate-500">{t("meetings.noComments")}</p>
        ) : (
          <ul className="space-y-2">
            {minutes.comments.map((c) => (
              <li key={c.id} className="bg-slate-50 rounded p-3 text-sm">
                <div className="font-medium">{c.author.firstName} {c.author.lastName} <span className="text-xs text-slate-500">· {formatDate(c.createdAt, i18n.language)}</span></div>
                <div className="text-slate-700 mt-1">{c.content}</div>
              </li>
            ))}
          </ul>
        )}
        {canEdit && !locked && (
          <div className="mt-3 flex gap-2">
            <input
              className="input flex-1"
              placeholder={t("meetings.addComment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && comment.trim()) commentMutation.mutate();
              }}
            />
            <button
              className="btn-primary"
              disabled={!comment.trim() || commentMutation.isPending}
              onClick={() => commentMutation.mutate()}
            >
              {t("meetings.addComment")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
